import { useEffect, useRef } from 'react';

import { TransactionOrderExpiredDialog } from 'components/TransactionOrderExpiredDialog/TransactionOrderExpiredDialog';
import type { ContentTiedToExpiredOrder } from 'pages/access_codes/components/TransactionProfile/TransactionProfile';
import { GroupData, GroupDetailsContext, useGroupDetails } from 'pages/groups/context';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { LicenseReturnable, TransactionApi } from 'apis/TransactionApi';
import { getFormattedDate, mapLicensesToGetOrderDetails } from 'utils';
import { TransactionDetailsContext } from 'pages/access_codes/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GMetrixBanner from 'components/GMetrixBanner/GMetrixBanner';
import { FormattedMessage, useIntl } from 'react-intl';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { GroupProfile } from 'pages/groups/components';
import { useNotification } from 'app/notifications';
import { InstructorApi } from 'apis/InstructorApi';
import { PageWithHeader } from 'components/layout';
import { helpContent } from 'pages/groups/help';
import { KendoIntlProvider } from 'app/culture';
import { CountryApi } from 'apis/CountryApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app/context';

import 'pages/groups/styles/main.scss';
import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

export const GroupDetail = () => {

    const { id } = useParams();
    const groupId = Number(id);
    const navigate = useNavigate();
    const location = useLocation();

    const [state, setState] = useSuperState({
        inactiveOrdersWContentOnOpenTransaction: [] as ContentTiedToExpiredOrder[],
        loading: true,
        transactionOrderExpiredDialogShow: false
    });

    const { inactiveOrdersWContentOnOpenTransaction, transactionOrderExpiredDialogShow } = state;

    const { open, pageContext, updateById } = useGroupDetails();
    const { formatMessage, locale } = useIntl();
    const { catchWithToast, createToast } = useNotification();
    const { highestRole, isSystemMode, selectedPartner } = useApp();

    const gmetrixBannerRef = useRef<any>(null);

    // PHASE 2: background-load the data the secondary tabs need (full student list, countries,
    // and — for classrooms with an access code — the available content library and inactive
    // orders) after the Details tab has rendered, then merge it into the open tab.
    const loadSecondaryGroupData = async (
        gId: number,
        partnerId: number | null,
        relatedTransactionId: number | null,
        licenseObj: LicenseReturnable
    ) => {

        try {

            const [students, countries] = await Promise.all([
                StudentGroupApi.GetAllStudents(gId, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_student_list.bb95052d', defaultMessage: 'Failed to load student list.' }))),
                CountryApi.GetAll().catch(catchWithToast(formatMessage({ id: 'failed_to_load_country_data.62b74554', defaultMessage: 'Failed to load country data.' })))
            ]);

            const dataToMergeIn: Partial<GroupData> = {
                countries: countries ?? [],
                secondaryLoading: false,
                studentList: students ?? [],
                students: []
            };

            if (relatedTransactionId !== null) {

                const [testContent, inactiveOrdersRaw] = await Promise.all([
                    TransactionApi.GetAllContentForTransactions(true).catch(catchWithToast(formatMessage({ id: 'failed_to_load_available_content.ba08850a', defaultMessage: 'Failed to load available content.' }))),
                    TransactionApi.GetInactiveOrdersByTransactionId(relatedTransactionId, 1, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_inactive_orders.2ab9bd25', defaultMessage: 'Failed to load inactive orders.' })))
                ]);

                let inactiveOrders = inactiveOrdersRaw;
                if (inactiveOrders && licenseObj) {

                    inactiveOrders = mapLicensesToGetOrderDetails(licenseObj.allLicenseHistory, [
                        ...inactiveOrders.map(order => order.OrderId)
                    ]);

                }

                dataToMergeIn.inactiveOrders = inactiveOrders ?? [];
                dataToMergeIn.testContent = testContent?.contentFlatList ?? [];

            }

            updateById(gId, dataToMergeIn);

        } catch (e) {

            console.error(e);

            // Always clear the loading flag so secondary tabs stop spinning even on failure.
            updateById(gId, { secondaryLoading: false });

        }

    };

    // `silent` re-pulls the classroom in place (e.g. after an edit) without showing the
    // full-page loader, so the open profile stays mounted until the fresh data swaps in.
    const loadGroup = async (silent = false) => {

        try {

            if (!silent) {

                setState({ loading: true });

            }
            gmetrixBannerRef.current?.removeBanner();

            const [allInstructors, group] = await Promise.all([
                InstructorApi.GetAll().catch(catchWithToast(formatMessage({ id: 'failed_to_load_instructor_data.15f58828', defaultMessage: 'Failed to load instructor data.' }))),
                StudentGroupApi.Get(groupId).catch(console.error)
            ]);

            if (!group) {

                // Not found / failed to load — fall back to the lookup screen and surface the error.
                createToast(formatMessage({ id: 'failed_to_load_classroom_details_please.54768062', defaultMessage: 'Failed to load classroom details. Please try again.' }), 'danger');
                navigate('/classrooms');
                return;

            }

            // Only a SysAdmin acting in their own system context (not managing another partner) scopes
            // by the classroom's true owner. In that case resolve the owner from the paged list (which
            // includes Partner) so the partner is saved for content scoping — mirroring the row-click
            // path. Anyone managing a partner (e.g. a Testing Center) or logged in as one scopes by the
            // currently managed partner instead.
            const isSysAdminInSystemMode = highestRole?.id === 1 && isSystemMode;

            if (isSysAdminInSystemMode) {

                const pagedGroups = await StudentGroupApi.GetAllPaged(1, 25, '', '', group.Control || group.Name)
                    .catch(catchWithToast(formatMessage({ id: 'failed_to_load_classroom_partner_data.de3dc054', defaultMessage: 'Failed to load classroom partner data.' })));
                const ownerPartner = pagedGroups?.Items?.[0]?.Partner;
                if (ownerPartner) {

                    group.Partner = ownerPartner;

                }

            }

            const partnerId = isSysAdminInSystemMode
                ? group.Partner?.Id ?? null
                : selectedPartner?.id ?? null;

            // Scope licenses to the resolved partner so the license data (and the enable-cloud check on
            // the content tab) reflects this classroom's partner, not necessarily the current
            // user/partner.
            const licenseObj = await TransactionApi.GetAllLicenses(partnerId)
                .catch(catchWithToast(formatMessage({ id: 'failed_to_load_license_data.ccdc93b9', defaultMessage: 'Failed to load license data.' })));

            if (!licenseObj) {

                // Not found / failed to load — fall back to the lookup screen and surface the error.
                createToast(formatMessage({ id: 'failed_to_load_classroom_details_please.54768062', defaultMessage: 'Failed to load classroom details. Please try again.' }), 'danger');
                navigate('/classrooms');
                return;

            }

            const [groupInstructors, transactionsAttachedToGroup] = await Promise.all([
                InstructorApi.Get(group.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_load_classroom_instructors.431aff20', defaultMessage: 'Failed to load classroom instructors.' }))),
                StudentGroupApi.GetTransactionsFromGroup(group.Id, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_classroom_access_codes.baba2569', defaultMessage: 'Failed to load classroom access codes.' })))
            ]);

            const mappedGroupDetails = { ...group, Instructors: groupInstructors ?? [] };

            let relatedTransactionId: number | null = null;
            let opened = false;

            if (transactionsAttachedToGroup && transactionsAttachedToGroup.length > 0) {

                // You can have multiple transactions hooked to a group, but for now we just want one.
                const relatedTransaction = transactionsAttachedToGroup[0];

                const [transactionDetails, assignedContentRaw] = await Promise.all([
                    TransactionApi.Get(relatedTransaction.Id, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_access_code_details.ed1f3281', defaultMessage: 'Failed to load access code details.' }))),
                    TransactionApi.GetLimitedContentByTransactionId(relatedTransaction.Id, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_assigned_content.3392953e', defaultMessage: 'Failed to load assigned content.' })))
                ]);

                let assignedContent = assignedContentRaw;

                if (assignedContent && assignedContent.length > 0) {

                    assignedContent = assignedContent.map(content => {

                        if (content.ExpirationDate && content.StartDate && content.ExpirationDateString) {

                            content.ExpirationDate = new Date(content.ExpirationDate);
                            content.ExpirationDateString = getFormattedDate(new Date(content.ExpirationDateString));
                            content.StartDate = new Date(content.StartDate);
                            content.StartDateString = getFormattedDate(new Date(content.StartDateString));

                        }
                        return content;

                    });

                }

                if (transactionDetails && transactionDetails.OrderIds && assignedContent && transactionDetails.ExpirationDate) {

                    const mappedTransactionDetails = {
                        ...transactionDetails,

                        ExpirationDate: transactionDetails.ExpirationDateString
                            ? new Date(transactionDetails.ExpirationDateString)
                            : undefined,
                        ExpirationDateString: transactionDetails.ExpirationDateString ? getFormattedDate(
                            new Date(transactionDetails.ExpirationDateString)
                        ) : null,

                        // The transaction Get does not return the owning partner; stamp it from the
                        // resolved classroom partner so downstream (shared) calls scope correctly
                        // instead of falling back to the System partner.
                        PartnerId: partnerId ?? transactionDetails.PartnerId,
                        StartDate: transactionDetails.StartDateString
                            ? new Date(transactionDetails.StartDateString)
                            : undefined,
                        StartDateString: transactionDetails.StartDateString ? getFormattedDate(
                            new Date(transactionDetails.StartDateString)
                        ) : null
                    };

                    relatedTransactionId = relatedTransaction.Id;
                    open({
                        Data: {
                            allInstructors: allInstructors ?? [],
                            content: assignedContent,
                            contentOriginal: assignedContent,
                            groupDetails: { ...mappedGroupDetails },
                            groupDetailsOriginal: { ...mappedGroupDetails },
                            licenseHistory: licenseObj.allLicenseHistory,
                            licenseRestrictions: licenseObj.licenses,
                            licenses: licenseObj.licenses,
                            secondaryLoading: true,
                            students: [],
                            transactionDetails: { ...mappedTransactionDetails },
                            transactionDetailsOriginal: { ...mappedTransactionDetails },
                            transactions: transactionsAttachedToGroup
                        },
                        HasChanges: false,
                        Id: group.Id,
                        Page: 'Groups',
                        Title: group.Name
                    });
                    opened = true;

                }

            }

            // No usable access code attached (or its details were incomplete) — open the
            // classroom with just the group-level data so the Details tab still renders.
            if (!opened) {

                open({
                    Data: {
                        allInstructors: allInstructors ?? [],
                        groupDetails: { ...mappedGroupDetails },
                        groupDetailsOriginal: { ...mappedGroupDetails },
                        licenseHistory: licenseObj.allLicenseHistory,
                        licenseRestrictions: licenseObj.licenses,
                        licenses: licenseObj.licenses,
                        secondaryLoading: true,
                        students: [],
                        transactions: transactionsAttachedToGroup ?? []
                    },
                    HasChanges: false,
                    Id: group.Id,
                    Page: 'Groups',
                    Title: group.Name
                });

            }

            setState({ loading: false });

            // PHASE 2: background-load the secondary tab data and merge it in.
            loadSecondaryGroupData(group.Id, partnerId, relatedTransactionId, licenseObj);

        } catch (e) {

            console.error(e);

            // Something went wrong loading the classroom — fall back to the lookup screen.
            createToast((e as Error)?.message || formatMessage({ id: 'failed_to_load_classroom_details.831bf87b', defaultMessage: 'Failed to load classroom details.' }), 'danger');
            navigate('/classrooms');

        }

    };

    const reloadGroup = async () => {

        await loadGroup(true);

    };

    useEffect(() => {

        loadGroup();

    }, [groupId]);

    // Surface a banner when the open classroom has content tied to an expired order.
    useEffect(() => {

        if (inactiveOrdersWContentOnOpenTransaction.length > 0) {

            gmetrixBannerRef.current?.createBanner(
                <span className="d-inline-flex align-items-center gap-2">
                    <FormattedMessage defaultMessage="There is an issue with one or more orders linked to this Classroom." id="there_is_an_issue_with_one_or_more_order.399a7879" />
                    <GMetrixButton
                        onClick={() => setState({ transactionOrderExpiredDialogShow: true })}
                        size="sm"
                        variant="danger"
                    >
                        <FormattedMessage defaultMessage="Resolve License Issue" id="resolve_license_issue.393fd474" />
                    </GMetrixButton>
                </span>
                , 'danger');

        } else {

            gmetrixBannerRef.current?.removeBanner();

        }

    }, [inactiveOrdersWContentOnOpenTransaction]);

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <div className={'group-details'} >

                        <GMetrixBanner ref={gmetrixBannerRef} />

                        <PageWithHeader
                            headerConfig={{
                                helpContent: helpContent,

                                // While a new classroom loads, pageContext still holds the previously
                                // opened tab. Show the name passed from the lookup page instantly and
                                // swap to the loaded title once pageContext matches the current route id.
                                title: pageContext?.Id === groupId
                                    ? (pageContext?.Title ?? '')
                                    : ((location.state as { title?: string } | null)?.title ?? ''),
                                trail: [{ label: formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' }), to: '/classrooms' }]
                            }}
                        >

                            {state.loading || !pageContext ?
                                (
                                    <div className={'d-flex align-items-center justify-content-center'} style={{ height: 300 }}>
                                        <GMetrixLoader />
                                    </div>
                                )
                                :
                                (

                                    <GroupProfile
                                        setInactiveOrdersWContentOnOpenTransaction={
                                            val => setState({ inactiveOrdersWContentOnOpenTransaction: typeof val === 'function' ? val(inactiveOrdersWContentOnOpenTransaction) : val })
                                        }
                                        contentTiedToInactiveOrder={inactiveOrdersWContentOnOpenTransaction.length > 0}
                                        createNewTransaction={false}
                                        onDetailsItemClick={async () => undefined}
                                        reloadGroup={reloadGroup}
                                        setCreateNewTransaction={() => undefined}
                                        {...pageContext}
                                    />

                                )
                            }

                        </PageWithHeader>

                        <TransactionOrderExpiredDialog
                            context="Classroom"
                            contextAccessCode={TransactionDetailsContext}
                            contextClassroom={GroupDetailsContext}
                            inactiveOrdersWContentOnOpenTransaction={inactiveOrdersWContentOnOpenTransaction}
                            reloadClassroom={reloadGroup}
                            setShow={val => setState({ transactionOrderExpiredDialogShow: typeof val === 'function' ? val(transactionOrderExpiredDialogShow) : val })}
                            show={transactionOrderExpiredDialogShow}
                        />
                    </div>
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
